import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, MailCheck } from "lucide-react";
import { LogoFull } from "@/components/Logo";
import { api, formatApiError } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email, origin_url: window.location.origin });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 grid-bg">
      <div className="absolute inset-0 hero-glow" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-md bg-[#0C1424] border border-slate-800 rounded-lg p-8"
      >
        <Link to="/" className="flex justify-center mb-6"><LogoFull size={92} /></Link>
        {sent ? (
          <div className="text-center" data-testid="forgot-success">
            <MailCheck size={40} className="text-cyanbrand mx-auto" />
            <h1 className="font-heading text-2xl font-semibold mt-4">Check your email</h1>
            <p className="text-slate-400 text-sm mt-2">
              If an account exists for <span className="text-slate-200">{email}</span>, we've sent a link to reset your password. It expires in 1 hour.
            </p>
            <Link to="/login" className="inline-block mt-6 text-cyanbrand hover:underline text-sm">Back to log in</Link>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-2xl font-semibold text-center">Reset your password</h1>
            <p className="text-slate-400 text-sm text-center mt-1">Enter your email and we'll send you a reset link.</p>
            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="forgot-form">
              <div>
                <label className="text-sm text-slate-400">Email</label>
                <input
                  data-testid="forgot-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-1 w-full bg-[#030812] border border-slate-800 text-white rounded-md px-4 py-3 focus:outline-none focus:ring-2 focus:ring-cyanbrand transition-all"
                  placeholder="you@company.com"
                />
              </div>
              {error && <p data-testid="forgot-error" className="text-sm text-red-400">{error}</p>}
              <button
                data-testid="forgot-submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-cyanbrand hover:bg-[#00B8D9] text-slate-900 font-semibold py-3 rounded-md transition-all disabled:opacity-60"
                style={{ boxShadow: "0 0 15px rgba(0,229,255,0.3)" }}
              >
                {loading && <Loader2 size={18} className="animate-spin" />} Send reset link
              </button>
            </form>
            <p className="text-sm text-slate-400 text-center mt-6">
              Remembered it? <Link to="/login" className="text-cyanbrand hover:underline">Back to log in</Link>
            </p>
          </>
        )}
      </motion.div>
    </div>
  );
}
